import {
  AUDIO_LAYERS,
  type AudioLayer,
  type ProceduralAudioController,
  type ProceduralAudioSnapshot,
} from "./audio.js";

export interface AudioControls {
  readonly element: HTMLElement;
  destroy(): Promise<void>;
}

const LAYER_LABELS: Readonly<Record<AudioLayer, string>> = Object.freeze({
  wind: "Procedural wind",
  waves: "Waves",
  whiteNoise: "White noise",
  chimes: "Sparse chimes",
});

const appendTextElement = (
  document: Document,
  parent: HTMLElement,
  tagName: string,
  text: string,
): HTMLElement => {
  const element = document.createElement(tagName);
  element.textContent = text;
  parent.append(element);
  return element;
};

export const renderAudioControls = (
  host: HTMLElement,
  controller: ProceduralAudioController,
): AudioControls => {
  const document = host.ownerDocument;
  const section = document.createElement("section");
  section.setAttribute("aria-label", "Ambient audio");

  const status = document.createElement("p");
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  status.setAttribute("aria-atomic", "true");
  section.append(status);

  const enable = appendTextElement(
    document,
    section,
    "button",
    "Enable ambience",
  ) as HTMLButtonElement;
  enable.type = "button";

  const off = appendTextElement(document, section, "button", "Off") as HTMLButtonElement;
  off.type = "button";

  const fieldset = document.createElement("fieldset");
  appendTextElement(document, fieldset, "legend", "Ambience layers");
  const inputs = new Map<AudioLayer, HTMLInputElement>();
  for (const layer of AUDIO_LAYERS) {
    const label = document.createElement("label");
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = controller.snapshot.layers[layer];
    label.append(input);
    appendTextElement(document, label, "span", LAYER_LABELS[layer]);
    fieldset.append(label);
    inputs.set(layer, input);
  }
  section.append(fieldset);

  let unavailable = false;
  const render = (snapshot: ProceduralAudioSnapshot): void => {
    status.textContent = snapshot.message;
    enable.hidden = unavailable || snapshot.status !== "off";
    enable.disabled = false;
    off.hidden = snapshot.status !== "on";
    off.disabled = false;
    for (const layer of AUDIO_LAYERS) {
      const input = inputs.get(layer);
      if (input !== undefined) input.checked = snapshot.layers[layer];
    }
  };

  const onEnable = (): void => {
    void controller.enableFromGesture().then(
      (enabled) => {
        if (!enabled && controller.snapshot.status === "off") {
          unavailable = true;
          render(controller.snapshot);
        }
      },
      () => {
        unavailable = true;
        render(controller.snapshot);
      },
    );
  };
  const onOff = (): void => {
    void controller.disable().catch(() => undefined);
  };
  enable.addEventListener("click", onEnable);
  off.addEventListener("click", onOff);

  const layerListeners = new Map<AudioLayer, () => void>();
  for (const layer of AUDIO_LAYERS) {
    const input = inputs.get(layer) as HTMLInputElement;
    const listener = (): void => {
      controller.setLayerEnabled(layer, input.checked);
    };
    input.addEventListener("change", listener);
    layerListeners.set(layer, listener);
  }

  const unsubscribe = controller.subscribe(render);
  host.append(section);
  let destroyPromise: Promise<void> | null = null;
  let bindingsRemoved = false;

  return Object.freeze({
    element: section,
    destroy(): Promise<void> {
      if (destroyPromise !== null) return destroyPromise;
      if (!bindingsRemoved) {
        bindingsRemoved = true;
        unsubscribe();
        enable.removeEventListener("click", onEnable);
        off.removeEventListener("click", onOff);
        for (const layer of AUDIO_LAYERS) {
          const input = inputs.get(layer);
          const listener = layerListeners.get(layer);
          if (input !== undefined && listener !== undefined) {
            input.removeEventListener("change", listener);
          }
        }
        section.parentNode?.removeChild(section);
      }
      const pending = controller.disable();
      destroyPromise = pending;
      void pending.then(
        () => undefined,
        () => {
          if (destroyPromise === pending) destroyPromise = null;
        },
      );
      return pending;
    },
  });
};
